import { DateTime } from "luxon";

// The timezone for user input (EST/EDT)
const APP_TIMEZONE = "America/New_York";

/**
 * Converts a user input (assumed to be in EST/EDT) to UTC for storage in the database
 * @param {string|Date} dateInput - Date input from user (could be from date picker, form, etc.)
 * @returns {string} ISO formatted date string in UTC
 */
export const toUTC = (dateInput) => {
  if (!dateInput) return null;
  
  // Handle different input types
  let dateTime;
  
  if (typeof dateInput === "string") {
    // Handle ISO strings
    if (dateInput.includes("T") && dateInput.includes("Z")) {
      // Already in ISO format with Z (UTC)
      dateTime = DateTime.fromISO(dateInput);
    } else if (dateInput.includes("T")) {
      // ISO format without Z - assume it's in local timezone (EST)
      dateTime = DateTime.fromISO(dateInput, { zone: APP_TIMEZONE });
    } else {
      // Just a date string - parse as EST
      dateTime = DateTime.fromFormat(dateInput, "yyyy-MM-dd", { zone: APP_TIMEZONE });
    }
  } else if (dateInput instanceof Date) {
    // JavaScript Date object - interpret as local time
    dateTime = DateTime.fromJSDate(dateInput, { zone: APP_TIMEZONE });
  } else {
    console.error("Unsupported date input format", dateInput);
    return null;
  }
  
  // Convert to UTC and return ISO string
  return dateTime.toUTC().toISO();
};

/**
 * Converts a UTC date from the database to EST for display
 * @param {string} utcDate - UTC date string from database
 * @returns {DateTime} Luxon DateTime object in EST timezone
 */
export const fromUTC = (utcDate) => {
  if (!utcDate) return null;
  
  // Parse the UTC date
  const dateTime = DateTime.fromISO(utcDate);
  
  // Convert to EST
  return dateTime.setZone(APP_TIMEZONE);
};

/**
 * Formats a date for display
 * @param {string} utcDate - UTC date string from database
 * @param {string} format - Luxon format string (default: "M/d/yy h:mm a 'est'")
 * @returns {string} Formatted date string in EST
 */
export const formatDate = (utcDate, format = "M/d/yy h:mm a 'est'") => {
  const dateTime = fromUTC(utcDate);
  if (!dateTime) return "";
  
  return dateTime.toFormat(format);
};

/**
 * Gets the current date and time in EST
 * @returns {DateTime} Current date and time as Luxon DateTime in EST
 */
export const getCurrentDateTime = () => {
  return DateTime.now().setZone(APP_TIMEZONE);
};

/**
 * For handling HTML date-time-local inputs 
 * @param {string} utcDate - UTC date string from database
 * @returns {string} Formatted for datetime-local input in EST (YYYY-MM-DDTHH:MM)
 */
export const toDateTimeLocalValue = (utcDate) => {
  const dateTime = fromUTC(utcDate);
  if (!dateTime) return "";
  
  return dateTime.toFormat("yyyy-MM-dd'T'HH:mm");
};

/**
 * Handles conversion from datetime-local input to UTC
 * @param {string} localValue - Value from datetime-local input (YYYY-MM-DDTHH:MM)
 * @returns {string} UTC ISO string for storage
 */
export const fromDateTimeLocalValue = (localValue) => {
  if (!localValue) return null;
  
  const dateTime = DateTime.fromFormat(
    localValue,
    "yyyy-MM-dd'T'HH:mm", 
    { zone: APP_TIMEZONE }
  );
  
  return dateTime.toUTC().toISO();
}; 